import {inject, Injectable, Renderer2, RendererFactory2} from '@angular/core';
import {Dialog, DialogRef} from '@angular/cdk/dialog';
import {
  ConnectedPosition,
  FlexibleConnectedPositionStrategy,
  GlobalPositionStrategy,
  Overlay,
} from '@angular/cdk/overlay';
import {ComponentType} from '@angular/cdk/portal';
import {BreakpointObserver, Breakpoints} from "@angular/cdk/layout";
import {AlertDialogComponent} from "./alert-dialog/alert-dialog.component";

@Injectable({
  providedIn: 'root'
})
export class OverlayService {

  private dialog = inject(Dialog);
  private overlay = inject(Overlay);
  private breakpointObserver = inject(BreakpointObserver);
  private rendererFactory = inject(RendererFactory2);

  private renderer!: Renderer2;

  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  private basePanelClass = ['bg-white', 'shadow-2'];

  modalMaxHeightClass = 'max-h-[80vh]';

  openNearElement<T>(
    component: ComponentType<T>,
    triggerElement: HTMLElement,
    options?: {
      positions?: ConnectedPosition[];
      disableClose?: boolean;
      data?: any;
      scrollStrategy?: 'noop' | 'block' | 'reposition' | 'close';
      isMobileResponsive?: boolean;
    }
  ): DialogRef<any, T> {

    const {
      positions = [
        {originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: 8}, // Default: Centered Below
        {originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -8}, // Fallback: Centered Above
        {originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 8}, // **New Fallback: Align Left if Overflowing Right**
        {originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -8}], // **New Fallback: Align Left if Overflowing Right and not enough space below**
      disableClose = false,
      data,
      scrollStrategy = 'block',
      isMobileResponsive = false
    } = options ?? {};

    const isMobile = this.breakpointObserver.isMatched([Breakpoints.XSmall, Breakpoints.Small]);

    let positionStrategy: FlexibleConnectedPositionStrategy | GlobalPositionStrategy | null;

    if (isMobile && isMobileResponsive) {
      positionStrategy = this.overlay.position()
        .global()
        .centerHorizontally()
        .centerVertically();
    } else {
      positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(triggerElement)
        .withFlexibleDimensions(false)
        .withPush(true)
        .withPositions(positions);
    }

    const scrollStrategyMapping = {
      noop: this.overlay.scrollStrategies.noop(),
      block: this.overlay.scrollStrategies.block(),
      reposition: this.overlay.scrollStrategies.reposition(),
      close: this.overlay.scrollStrategies.close(),
    };

    const appliedScrollStrategy = scrollStrategyMapping[scrollStrategy] ?? this.overlay.scrollStrategies.block();

    let dialogRef = this.dialog.open(component, {
      positionStrategy: positionStrategy,
      scrollStrategy: appliedScrollStrategy,
      disableClose: disableClose,
      backdropClass: ['bg-black/5', 'overflow-clip'],
      panelClass: [...this.basePanelClass, 'rounded-3xl'],
      data: data,
    });

    return dialogRef;

  }

  openAlert(title: string, message: string): DialogRef<any, AlertDialogComponent> {
    let dialogRef = this.dialog.open(AlertDialogComponent, {
      width: '300px',
      data: {
        title: title,
        message: message
      },
      panelClass: [...this.basePanelClass, 'rounded-3xl']
    });

    return dialogRef;
  }

  openModal<T>(component: ComponentType<T>, options?: {
    disableClose?: boolean,
    maxHeightClass?: string,
    data?: any
  }):
    DialogRef<any, T> {

    const {disableClose = false, maxHeightClass, data} = options ?? {};

    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .centerVertically();

    let dialogRef = this.dialog.open(component, {
      positionStrategy: positionStrategy,
      disableClose: disableClose,
      backdropClass: ['bg-black/20'],
      panelClass: [...this.basePanelClass, maxHeightClass ?? this.modalMaxHeightClass, 'rounded-3xl', 'sm:w-4/5', 'md:w-3/5', 'lg:w-2/5'],
      data: data
    });

    this.setOverlayMaxHeight(maxHeightClass ?? this.modalMaxHeightClass);

    return dialogRef;
  }

  private setOverlayMaxHeight(maxHeightClass: string): void {
    const overlayElement = document.querySelector('.base-overlay') as HTMLElement;
    if (overlayElement) {
      this.renderer.addClass(overlayElement, maxHeightClass);
    }
  }

  openBackdrop<T>(
    component: ComponentType<T>,
    options?: {
      disableClose?: boolean;
      data?: any;
    }
  ): DialogRef<any, T> {
    const {disableClose = false, data} = options ?? {};

    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .bottom('0px');

    let dialogRef = this.dialog.open(component, {
      positionStrategy: positionStrategy,
      disableClose: disableClose,
      panelClass: [...this.basePanelClass, 'w-[100%]', 'h-[90%]', 'rounded-t-3xl', 'overflow-clip'],
      data: data
    });

    return dialogRef;
  }

  openBottomSheet<T>(
    component: ComponentType<T>,
    options?: {
      disableClose?: boolean;
      data?: any;
    }
  ): DialogRef<any, T> {
    const {disableClose = false, data} = options ?? {};

    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .bottom('0px');

    let dialogRef = this.dialog.open(component, {
      positionStrategy: positionStrategy,
      disableClose: disableClose,
      panelClass: [...this.basePanelClass, 'w-max-[300px]', 'h-[70%]', 'rounded-t-3xl', 'overflow-clip', 'overflow-y-scroll'],
      data: data
    });

    return dialogRef;
  }

  openFullScreen<T>(
    component: ComponentType<T>,
    options?: {
      disableClose?: boolean;
      data?: any;
    }
  ): DialogRef<any, T> {
    const {disableClose = false, data} = options ?? {};

    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .bottom('0px');

    let dialogRef = this.dialog.open(component, {
      positionStrategy: positionStrategy,
      disableClose: disableClose,
      panelClass: [...this.basePanelClass, 'w-dvw', 'h-dvh', 'overflow-clip', 'overflow-y-scroll'],
      data: data
    });

    return dialogRef;
  }

  openSidePanelRight<T>(
    component: ComponentType<T>,
    options?: {
      widthInPx?: number;
      disableClose?: boolean;
      data?: any;
    }
  ): DialogRef<any, T> {
    const {
      widthInPx = 350,
      disableClose = false,
      data
    } = options ?? {};

    const positionStrategy = this.overlay
      .position()
      .global()
      .top('0px')
      .right('0px')
      .width(`${widthInPx}px`);

    let dialogRef = this.dialog.open(component, {
      positionStrategy: positionStrategy,
      disableClose: disableClose,
      panelClass: [...this.basePanelClass, 'h-dvh', 'w-full', 'overflow-clip', 'overflow-y-scroll'],
      data: data
    });

    return dialogRef;
  }

  openSidePanelLeft<T>(
    component: ComponentType<T>,
    options?: {
      widthInPx?: number;
      disableClose?: boolean;
      data?: any;
    }
  ): DialogRef<any, T> {
    const {
      widthInPx = 350,
      disableClose = false,
      data
    } = options ?? {};

    const positionStrategy = this.overlay
      .position()
      .global()
      .top('0px')
      .left('0px')
      .width(`${widthInPx}px`);

    let dialogRef = this.dialog.open(component, {
      positionStrategy: positionStrategy,
      disableClose: disableClose,
      panelClass: [...this.basePanelClass, 'h-dvh', 'w-full', 'overflow-clip', 'overflow-y-scroll'],
      data: data
    });

    return dialogRef;
  }

}
